import axios from "axios";
import { useEffect, useState } from "react";

/* ────────────────────────────────────────────────────────────────
   Quran verse helpers for Master Admin.

   CMZ library QURAN quotes store only surah/ayah; Arabic + a preview
   translation are fetched on demand. Admin has no TV language, so we
   default to Sahih International (English).

   Calls go through cmz-backend `/api/quran/proxy/content/api/v4/...`
   (Quran Foundation Content API; token injected server-side). Never hit
   api.quran.com from the browser.

   Proxy base is derived from VITE_GRAPHQL_URL
   (strip /graphql → /api/quran/proxy).
   ──────────────────────────────────────────────────────────────── */

export const DEFAULT_TRANSLATION_ID = 20; // Sahih International

const CONTENT_V4_PREFIX = "content/api/v4";

/** Host that mounts `/api/quran/*` (cmz-backend / graphql-server). */
export function getQuranProxyBaseUrl(): string {
  const graphql =
    import.meta.env.VITE_GRAPHQL_URL?.trim() ||
    "https://graphql.staging.connectmazjid.com/graphql";
  const origin = graphql.replace(/\/graphql\/?$/, "");
  return `${origin}/api/quran/proxy`;
}

/** Content API v4 base via the backend proxy. */
export function getQuranContentV4Base(): string {
  return `${getQuranProxyBaseUrl()}/${CONTENT_V4_PREFIX}`;
}

/**
 * Absolute URL under content/api/v4.
 * Prefer this over hardcoding paths so every call stays on the proxy.
 */
export function quranContentUrl(path: string): string {
  return `${getQuranContentV4Base()}/${path.replace(/^\//, "")}`;
}

export const stripHtml = (s: string) => s.replace(/<[^>]+>/g, "");

const VERSE_KEY_RE = /^\s*(\d{1,3})\s*[:\s.-]\s*(\d{1,3})\s*$/;
export const parseVerseKey = (q: string): string | null => {
  const m = q.match(VERSE_KEY_RE);
  if (!m) return null;
  const ch = Number(m[1]);
  const v = Number(m[2]);
  if (ch < 1 || ch > 114 || v < 1) return null;
  return `${ch}:${v}`;
};

export type VerseText = { arabic: string; english: string; translator: string };

// Module-level cache so the same verse fetched by multiple cards hits the
//   network once. Verse text never changes, so we cache for the session.
const cache = new Map<string, VerseText>();
const inflight = new Map<string, Promise<VerseText | null>>();

export async function fetchVerseText(
  surah: number,
  ayah: number
): Promise<VerseText | null> {
  const key = `${surah}:${ayah}`;
  const cached = cache.get(key);
  if (cached) return cached;
  const running = inflight.get(key);
  if (running) return running;

  const p = axios
    .get(quranContentUrl(`verses/by_key/${key}`), {
      params: { translations: DEFAULT_TRANSLATION_ID, fields: "text_uthmani" },
    })
    .then((res) => {
      const v = res.data?.verse;
      if (!v) return null;
      const data: VerseText = {
        arabic: stripHtml(v.text_uthmani || v.text || ""),
        english: stripHtml(v.translations?.[0]?.text || ""),
        translator: v.translations?.[0]?.resource_name || "",
      };
      cache.set(key, data);
      return data;
    })
    .catch(() => null)
    .finally(() => inflight.delete(key));

  inflight.set(key, p);
  return p;
}

/** React hook: resolves verse text for a surah/ayah (no-op when either is null). */
export function useVerseText(surah?: number | null, ayah?: number | null) {
  const key = surah && ayah ? `${surah}:${ayah}` : null;
  const [data, setData] = useState<VerseText | null>(() =>
    key ? cache.get(key) ?? null : null
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!surah || !ayah) {
      setData(null);
      setLoading(false);
      return;
    }
    const cached = cache.get(`${surah}:${ayah}`);
    if (cached) {
      setData(cached);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    fetchVerseText(surah, ayah).then((d) => {
      if (!alive) return;
      setData(d);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [surah, ayah]);

  return { data, loading };
}
